import { Box, TableCell, TableRow } from "@mui/material";
import { DotLegalButton, DotLegalDialog, DotLegalHeader, DotLegalTable, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection";
import LinkedControlsSection from "../../riskControls/LinkedControlsSection";
import { RiskScenarioRow } from "../RiskAssessmentsOverview.types";
import { useTreatmentDialogStyles } from "../treatmentDialogs/TreatmentDialogs.styles";
import MitigationPlanFormFields from "../treatmentDialogs/MitigationPlanFormFields";
import { useBulkMitigationDialog } from "./BulkMitigationDialog.hooks";

export interface IBulkMitigationDialog {
    scenarios: Array<RiskScenarioRow>;
    onRemoveScenario: (id: string) => void;
    onSaved: () => void;
    onClose: () => void;
}

function BulkMitigationDialog(props: IBulkMitigationDialog) {
    const { translateString } = useTranslation();
    const styles = useTreatmentDialogStyles();

    const { planForm, scenarios, removeScenario, isValid, inProgress, onSave, consequenceOptions, probabilityOptions } = useBulkMitigationDialog(props);

    const scenarioHeaders: Array<ITableHeader<RiskScenarioRow>> = [
        { property: "name", text: translateString("scenario") },
        { property: null, text: "", align: "right", notSortable: true },
    ];

    return (
        <DotLegalDialog
            open
            size="md"
            inProgress={inProgress}
            header={translateString("bulkMitigation")}
            buttonOkText={translateString("save")}
            onOkClick={onSave}
            okButtonDisabled={!isValid}
            secondaryButtonText={translateString("cancel")}
            secondaryButtonStyle="secondary"
            onSecondaryButtonClick={props.onClose}
            onDialogClose={props.onClose}
        >
            <Box sx={styles.content}>
                <Box sx={styles.subHeader}>
                    <DotLegalHeader center marginTop={-1} marginBottom={0} fontWeight={400} headerStyle="small">
                        {translateString("bulkMitigationDescription")}
                    </DotLegalHeader>
                </Box>

                <DotLegalSection>
                    <DotLegalHeader headerStyle="small" color="primary" marginBottom={1}>
                        {translateString("bulkMitigationSelectedScenarios", { count: scenarios.length })}
                    </DotLegalHeader>
                    <DotLegalTable
                        headers={scenarioHeaders}
                        data={scenarios}
                        defaultOrderBy="name"
                        defaultOrder="asc"
                        hidePagination
                        clickableRows={false}
                        emptyText={translateString("noData")}
                        renderRow={(row) => (
                            <TableRow key={row.id}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell align="right">
                                    <DotLegalButton buttonType="linkButton" size="small" onClick={() => removeScenario(row.id)}>
                                        {translateString("remove")}
                                    </DotLegalButton>
                                </TableCell>
                            </TableRow>
                        )}
                    />
                </DotLegalSection>

                <MitigationPlanFormFields fields={planForm} consequenceOptions={consequenceOptions} probabilityOptions={probabilityOptions} />

                {planForm.showLinkedControls && (
                    <LinkedControlsSection
                        linkedControlIds={planForm.frameworkControlIds}
                        availableControls={planForm.allControls}
                        onChange={planForm.setFrameworkControlIds}
                        heading="controlsAddedByThisPlan"
                    />
                )}
            </Box>
        </DotLegalDialog>
    );
}

export default BulkMitigationDialog;
